// @ts-nocheck
import {
  AutocompleteOptions,
  AutocompleteState,
  createAutocomplete,
} from "@algolia/autocomplete-core";
import React from "react";
// import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import Collection from "@/components/ui/Autocomplete/Collection";
import FormComponent from "@/components/ui/Autocomplete/FormComponent";
import { clsx } from "@cgc/ui/utils/clsx";

export type AutocompleteProps = AutocompleteOptions<any> & {
  className?: string;
  query?: string | null;
  empty?: (state: AutocompleteState<any>) => React.ReactNode;
};

// FIXME: proper autocomplete types
const Autocomplete = ({
  getSources,
  className,
  onSubmit,
  query,
  // empty,
  ...props
}: AutocompleteProps) => {
  const [autocompleteState, setAutocompleteState] = React.useState<
    AutocompleteState<any>
  >({
    ...(query ? { query } : null),
  });

  const autocomplete = React.useMemo(
    () =>
      createAutocomplete({
        getSources,
        onSubmit,
        initialState: autocompleteState,
        ...props,
        onStateChange({ state }) {
          props.onStateChange && props.onStateChange({ state });
          setAutocompleteState(state);
        },
      }),
    [autocompleteState, getSources, onSubmit, props],
  );

  const inputRef = React.useRef(null);
  const formRef = React.useRef(null);
  const panelRef = React.useRef(null);

  const { getEnvironmentProps } = autocomplete;

  React.useEffect(() => {
    if (!(formRef.current && panelRef.current && inputRef.current)) {
      return;
    }

    const { onTouchStart, onTouchMove, onMouseDown } = getEnvironmentProps({
      formElement: formRef.current,
      panelElement: panelRef.current,
      inputElement: inputRef.current,
    });

    window.addEventListener("touchstart", onTouchStart);
    window.addEventListener("touchmove", onTouchMove);
    window.addEventListener("mousedown", onMouseDown);

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("mousedown", onMouseDown);
    };
  }, [getEnvironmentProps, autocompleteState?.isOpen]);

  // handle clicks outside autocomplete
  React.useEffect(() => {
    const handleClick = (event: Event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        autocomplete.setIsOpen(false);
        autocomplete.setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClick);

    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [autocomplete, panelRef]);

  const formProps = autocomplete.getFormProps({
    inputElement: inputRef.current,
  });
  const inputProps = autocomplete.getInputProps({ placeholder: "Search..." });

  // handle query reset from search components
  React.useEffect(() => {
    if (!query) {
      autocomplete.setQuery("");
    }
  }, [query]);

  return (
    <FormComponent
      ref={inputRef}
      root={{ className, props: autocomplete.getRootProps({}) }}
      form={{ props: formProps }}
      input={{ props: inputProps }}
      showIcon={!autocompleteState.query}
    >
      {autocompleteState?.isOpen && (
        <div
          ref={panelRef}
          className={clsx(
            "shadow-autocomplete relative z-10 mx-[10px] mt-1 rounded-[5px] bg-white sm:mx-[15px]",
            // autocompleteState.status === "stalled" && "aa-Panel--stalled"
          )}
          {...autocomplete.getPanelProps({})}
        >
          {autocompleteState?.collections.map((collection, index) => (
            <Collection
              key={`source_${index}`}
              source={collection.source}
              items={collection.items}
              autocomplete={autocomplete}
            />
          ))}
          {/*: empty && empty(autocompleteState)}*/}
        </div>
      )}
    </FormComponent>
  );
};

export default Autocomplete;
